import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from 'prop-types';
import axios from "axios";
import { GiWingfoot } from "react-icons/gi";
import { CheckCircle2, Loader2, SendHorizonal, MoreHorizontal, MessageSquare, Mic } from "lucide-react";
import { useLoadingStore } from "../../lib/stores/stores";
import { ChatBubble, ChatBubbleAvatar, ChatBubbleMessage } from "@/components/ui/chat/chat-bubble";
import { ChatMessageList } from "@/components/ui/chat/chat-message-list";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import useDetectKeyboardOpen from "use-detect-keyboard-open";
import { 
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
// Import the component (type imports not needed in JS)
import { OrbitalDataCloud } from "@/components/ui/orbital-data-cloud";

// Constants (re-including for context, no changes here)
const SENDER_BOT = "bot";
const SENDER_USER = "user";
const BOT_TYPING_MESSAGE_PLACEHOLDER = " ";
const API_ERROR_MESSAGE = "Sorry, I encountered an error. Please try again.";
const API_ENDPOINT = "https://master-hermes-backend-lqfbwlkw2a-uc.a.run.app/api/v1/project/core/process_request";
const MAX_TEXTAREA_HEIGHT = 150;
const MOBILE_NAVBAR_OFFSET_CLASS = "bottom-16";
const WELCOME_TEXT_HEIGHT_BUFFER = 5;

const LOADING_STEPS_DATA = [
  { id: 'init_lang', text: 'Initializing Langchain...' },
  { id: 'gen_emb', text: 'Generating Text Embeddings...' },
  { id: 'init_rag', text: 'Initializing RAG System...' },
  { id: 'make_mcp', text: 'Connecting to Model Context Protocol...' },
  { id: 'init_tts', text: 'Initializing Text-to-Speech...' },
];

const ANIMATION_STEP_INTERVAL = 1000;
const ANIMATION_STEP_COMPLETED_HOLD_DURATION = 200;
const ANIMATION_STEP_FADE_OUT_DURATION = 400;
const ANIMATION_VIEW_TRANSITION_DURATION = 500;
const ANIMATION_WELCOME_TEXT_APPEAR_BUFFER = 50;

const SINGLE_STEP_RENDERED_HEIGHT = 42;
const STEP_SPACING = 10;

const MIN_TEXTAREA_HEIGHT = 40;

/**
 * @typedef {Object} ChatInputProps
 * @property {string} value
 * @property {function(Event): void} onChange
 * @property {function(): void} onSubmit
 * @property {string} [placeholder]
 * @property {boolean} [isLoading]
 * @property {string} [className]
 * @property {'chat'|'voice'} responseMode
 * @property {function(): void} onToggleMode
 */

/**
 * ChatInput component for the chat interface
 * @param {ChatInputProps} props
 */
const ChatInput = ({ 
  value, 
  onChange, 
  onSubmit, 
  placeholder = "Type your message...", 
  isLoading = false, 
  className 
}) => {
  const textareaRef = useRef(null);


  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleInternalChange = useCallback((e) => {
    setInternalValue(e.target.value);
    onChange(e);
  }, [onChange]);

  const isInputDisabled = isLoading;
  const isSendButtonDisabled = isLoading || !internalValue.trim();

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      let targetHeight;

      if (internalValue.trim() === "") {
        targetHeight = MIN_TEXTAREA_HEIGHT;
      } else {
        targetHeight = Math.max(MIN_TEXTAREA_HEIGHT, scrollHeight);
      }
      targetHeight = Math.min(targetHeight, MAX_TEXTAREA_HEIGHT);

      textareaRef.current.style.height = `${targetHeight}px`;
      textareaRef.current.style.overflowY = targetHeight >= MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
    }
  }, [internalValue]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !isLoading && internalValue.trim()) {
      e.preventDefault();
      onSubmit();
    }
  };

  const handleSubmit = (e) => {
    if (e) e.preventDefault();
    if (!isLoading && internalValue.trim()) {
      onSubmit();
    }
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      className={cn(
        "flex w-full items-end rounded-md bg-white border focus-within:outline focus-within:outline-gray-500 focus-within:outline-offset-0 overflow-hidden",
        "transition-all duration-500 ease-in-out", // For glow subsidence
        isLoading 
          ? "shadow-[0_4px_12px_-2px_rgba(239,68,68,0.4)] border-red-400" // RED Glow effect
          : "shadow-none border-gray-200", // Default state
        className
      )}
    >
      <SheetTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-gray-500 hover:text-black focus-visible:ring-0 h-10 w-10 rounded-none border-r border-gray-200"
          aria-label="Open settings"
        >
          <MoreHorizontal className="h-5 w-5" />
        </Button>
      </SheetTrigger>

      <Textarea
        ref={textareaRef}
        value={internalValue}
        onChange={handleInternalChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isInputDisabled}
        rows={1}
        className={cn(
          "flex-1 resize-none border-0 focus-visible:ring-0 bg-transparent placeholder:text-gray-400 text-sm py-2.5 px-3",
          isInputDisabled && "cursor-not-allowed opacity-50"
        )}
        style={{
          minHeight: `${MIN_TEXTAREA_HEIGHT}px`,
          maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
          overflowY: 'hidden'
        }}
      />
      <Button
        type="submit"
        variant="ghost"
        disabled={isSendButtonDisabled}
        size="icon"
        className={cn(
          "shrink-0 text-gray-500 hover:text-black focus-visible:ring-0 h-10 w-10 rounded-none",
          isSendButtonDisabled && "cursor-not-allowed opacity-50"
        )}
      >
        <SendHorizonal className="h-5 w-5" />
        <span className="sr-only">Send message</span>
      </Button>
    </form>
  );
};
ChatInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isLoading: PropTypes.bool,
  className: PropTypes.string
};

// --- TypingIndicator Component ---
const TypingIndicator = () => (
  <div className="flex items-center space-x-1 text-gray-800">
    <span>Typing</span>
    <span className="h-1.5 w-1.5 bg-gray-600 rounded-full animate-bounce-dot [animation-delay:-0.3s] ml-1"></span>
    <span className="h-1.5 w-1.5 bg-gray-600 rounded-full animate-bounce-dot [animation-delay:-0.15s]"></span>
    <span className="h-1.5 w-1.5 bg-gray-600 rounded-full animate-bounce-dot"></span>
  </div>
);

/**
 * @typedef {Object} ChatMessage
 * @property {string} id
 * @property {string} message
 * @property {'user'|'bot'} sender
 * @property {Date} timestamp
 * @property {boolean} [isTyping]
 */

// --- ChatComponent ---
const ChatComponent = () => {
  const calculatedStepsContainerHeight = useMemo(() => {
    return LOADING_STEPS_DATA.length * SINGLE_STEP_RENDERED_HEIGHT +
           (LOADING_STEPS_DATA.length > 0 ? (LOADING_STEPS_DATA.length - 1) * STEP_SPACING : 0);
  }, []);

  // Load messages from localStorage on initial render
  const [messages, setMessages] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedMessages = localStorage.getItem('hermes_chat_messages');
      return savedMessages ? JSON.parse(savedMessages) : [];
    }
    return [];
  });
  
  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('hermes_chat_messages', JSON.stringify(messages));
    }
  }, [messages]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [showInitialOverlay, setShowInitialOverlay] = useState(() => {
    // Skip initial overlay if we have existing messages
    if (typeof window !== 'undefined') {
      const savedMessages = localStorage.getItem('hermes_chat_messages');
      return !(savedMessages && JSON.parse(savedMessages).length > 0);
    }
    return true;
  });
  const messagesEndRef = useRef(null);
  const inputAreaRef = useRef(null);
  const welcomeTextContentRef = useRef(null);

  const [animatedStepStates, setAnimatedStepStates] = useState(
    LOADING_STEPS_DATA.map(step => ({ ...step, status: 'processing', isFading: false }))
  );
  const [activelyShowingWelcome, setActivelyShowingWelcome] = useState(false);
  const [welcomeTextActualHeight, setWelcomeTextActualHeight] = useState(0);
  const isLoadingApi = useLoadingStore((state) => state.loading);
  
  const isKeyboardOpen = useDetectKeyboardOpen();
  const [isMobileView, setIsMobileView] = useState(false);
  const [messageListPaddingBottom, setMessageListPaddingBottom] = useState(0);

  const [isProcessingMessage, setIsProcessingMessage] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [responseMode, setResponseMode] = useState('chat');

  const orbitalDataCloudRef = useRef(null);
  const lastBotMessageIdRef = useRef(null);

  const lastUserMessage = useMemo(() => {
    const userMsgs = messages.filter(msg => msg.sender === SENDER_USER);
    return userMsgs.length > 0 ? userMsgs[userMsgs.length - 1].message : null;
  }, [messages]);

  useEffect(() => {
    const checkMobileView = () => setIsMobileView(window.innerWidth < 768);
    checkMobileView();
    window.addEventListener("resize", checkMobileView);
    return () => window.removeEventListener("resize", checkMobileView);
  }, []);

  useEffect(() => {
    if (inputAreaRef.current && !showInitialOverlay) {
      const buffer = 8;
      setMessageListPaddingBottom(inputAreaRef.current.offsetHeight + buffer);
    } else if (showInitialOverlay) {
      setMessageListPaddingBottom(0);
    }
  }, [isMobileView, isKeyboardOpen, currentMessage, showInitialOverlay, isLoadingApi, messages]);

  useEffect(() => {
    useLoadingStore.setState({ loading: false });
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage) {
      if (lastMessage.sender === SENDER_BOT) {
        if (!lastMessage.isLoading) { 
          if (lastMessage.id !== lastBotMessageIdRef.current) { 
            if (responseMode === 'voice') {
              orbitalDataCloudRef.current?.playAudio();
            } else {
              orbitalDataCloudRef.current?.pauseAudio();
            }
            lastBotMessageIdRef.current = lastMessage.id; 
          }
        } else { 
          orbitalDataCloudRef.current?.pauseAudio();
        }
      } else if (lastMessage.sender === SENDER_USER) { 
        orbitalDataCloudRef.current?.pauseAudio();
      }
    } else {
      orbitalDataCloudRef.current?.pauseAudio();
    }
  }, [messages, responseMode]); // Added responseMode to dependency array

  useEffect(() => {
    // This effect seems redundant given the one above.
    // Consider merging or clarifying its purpose.
    // If it's just for initial setup or mode toggling:
    if (orbitalDataCloudRef.current) { // Check if ref is mounted
        if (responseMode === 'voice' && messages.length > 0 && messages[messages.length-1].sender === SENDER_BOT && !messages[messages.length-1].isLoading) {
           // Potentially play if last message was bot and not loading, and now switching to voice
           // However, the above useEffect already handles playing on new bot messages in voice mode.
        } else {
          orbitalDataCloudRef.current.pauseAudio();
        }
    }
  }, [responseMode, messages]);


  useEffect(() => {
    if (showInitialOverlay) {
      const stepProcessingTimers = [];
      const stepVisualEffectTimers = [];

      setAnimatedStepStates(
        LOADING_STEPS_DATA.map(step => ({ ...step, status: 'processing', isFading: false }))
      );
      setActivelyShowingWelcome(false);
      setWelcomeTextActualHeight(0);

      LOADING_STEPS_DATA.forEach((stepData, index) => {
        stepProcessingTimers.push(
          setTimeout(() => {
            setAnimatedStepStates(prevStates =>
              prevStates.map(s => s.id === stepData.id ? { ...s, status: 'completed' } : s)
            );
            const fadeTimer = setTimeout(() => {
              setAnimatedStepStates(prevStates =>
                prevStates.map(s => s.id === stepData.id ? { ...s, isFading: true } : s)
              );
            }, ANIMATION_STEP_COMPLETED_HOLD_DURATION);
            stepVisualEffectTimers.push(fadeTimer);
          }, (index + 1) * ANIMATION_STEP_INTERVAL)
        );
      });

      const lastItemIndividualFadeEndTime =
        (LOADING_STEPS_DATA.length * ANIMATION_STEP_INTERVAL) +
        ANIMATION_STEP_COMPLETED_HOLD_DURATION +
        ANIMATION_STEP_FADE_OUT_DURATION;

      const showWelcomeContentTimer = setTimeout(() => {
        setActivelyShowingWelcome(true);
        requestAnimationFrame(() => {
          if (welcomeTextContentRef.current) {
            const height = welcomeTextContentRef.current.getBoundingClientRect().height;
            setWelcomeTextActualHeight(height + WELCOME_TEXT_HEIGHT_BUFFER);
          }
        });
      }, lastItemIndividualFadeEndTime + ANIMATION_WELCOME_TEXT_APPEAR_BUFFER);

      stepProcessingTimers.push(showWelcomeContentTimer);

      return () => {
        stepProcessingTimers.forEach(clearTimeout);
        stepVisualEffectTimers.forEach(clearTimeout);
      };
    }
  }, [showInitialOverlay]);

  const currentDynamicContentHeight = useMemo(() => {
    if (activelyShowingWelcome) {
      return welcomeTextActualHeight > 0 ? `${welcomeTextActualHeight}px` : `${calculatedStepsContainerHeight}px`;
    }
    return `${calculatedStepsContainerHeight}px`;
  }, [activelyShowingWelcome, welcomeTextActualHeight, calculatedStepsContainerHeight]);

  const handleInputChange = useCallback((e) => {
    setCurrentMessage(e.target.value);
  }, []);

  // Handle response mode change from the sidebar
  const handleResponseModeChange = useCallback((isVoice) => {
    setResponseMode(isVoice ? 'voice' : 'chat');
  }, []);

  const handleClearChat = useCallback(() => {
    setMessages([]);
    setCurrentMessage('');
    setShowInitialOverlay(true);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('hermes_chat_messages');
    }
    orbitalDataCloudRef.current?.pauseAudio(); // Stop audio on clear
    setTimeout(() => setIsSidebarOpen(false), 300);
  }, []);

  const handleInputSubmit = useCallback(async () => {
    const trimmedMessage = currentMessage.trim();
    if (!trimmedMessage) return;

    if (showInitialOverlay) {
      setShowInitialOverlay(false);
    }

    const userMsg = { id: crypto.randomUUID(), message: trimmedMessage, sender: SENDER_USER, timestamp: new Date(), isLoading: false };
    const loadingMsgId = crypto.randomUUID();
    const botLoadingMsg = { id: loadingMsgId, message: BOT_TYPING_MESSAGE_PLACEHOLDER, sender: SENDER_BOT, timestamp: new Date(), isLoading: true };

    setMessages(prev => [...prev, userMsg, botLoadingMsg]);
    setCurrentMessage("");
    setIsProcessingMessage(true); 
    useLoadingStore.setState({ loading: true }); 
    orbitalDataCloudRef.current?.pauseAudio(); 


    try {
      const response = await axios.get(API_ENDPOINT, { 
        params: { request_text: trimmedMessage },
        withCredentials: false
      });
      setMessages(prev => prev.map(msg =>
        msg.id === loadingMsgId ? { ...msg, message: response.data.message || API_ERROR_MESSAGE, isLoading: false, timestamp: new Date() } : msg
      ));
    } catch (err) {
      console.error("API Error:", err);
      const errorMessage = (err.response && err.response.data && err.response.data.message) || API_ERROR_MESSAGE;
      setMessages(prev => prev.map(msg =>
        msg.id === loadingMsgId ? { ...msg, message: errorMessage, isLoading: false, timestamp: new Date() } : msg
      ));
    } finally {
      setIsProcessingMessage(false);
      useLoadingStore.setState({ loading: false }); 
    }
  }, [currentMessage, showInitialOverlay]);

  return (
    <div className="relative flex flex-col w-full h-screen bg-white antialiased">
      {/* Initial Overlay */}
      <div
        className={cn(
          `absolute inset-0 z-10 bg-white flex flex-col items-center justify-center p-6 text-center`,
          showInitialOverlay ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none delay-[600ms]'
        )}
      >
        {showInitialOverlay && (
          <>
            <GiWingfoot className="h-14 w-14 text-gray-600 mb-6 shrink-0" />
            
            <div
              className={cn(
                "relative w-full max-w-sm md:max-w-md text-left overflow-hidden",
                `transition-[height] ease-in-out duration-[${ANIMATION_VIEW_TRANSITION_DURATION}ms]`
              )}
              style={{ height: currentDynamicContentHeight }}
            >
              {/* Steps View */}
              <div
                className={cn(
                  "absolute inset-0 transition-opacity ease-in-out",
                  `duration-[${ANIMATION_VIEW_TRANSITION_DURATION}ms]`,
                  !activelyShowingWelcome ? "opacity-100" : "opacity-0 pointer-events-none -z-10"
                )}
              >
                <div 
                  className="w-full flex flex-col justify-start"
                  style={{ height: `${calculatedStepsContainerHeight}px`}}
                >
                  <div className="space-y-2.5">
                    {animatedStepStates.map((step, index) => (
                      <div
                        key={step.id}
                        className={cn(
                          "flex items-center px-3 rounded-lg border overflow-hidden",
                          "transition-all ease-in-out", `duration-[${ANIMATION_STEP_FADE_OUT_DURATION}ms]`,
                          step.isFading
                            ? 'opacity-0 max-h-0 !py-0 !mt-0 !mb-0 border-transparent pointer-events-none'
                            : `py-2.5 h-[${SINGLE_STEP_RENDERED_HEIGHT}px] max-h-[${SINGLE_STEP_RENDERED_HEIGHT}px]`,
                          !step.isFading && step.status === 'completed'
                            ? 'bg-green-50 border-green-200'
                            : !step.isFading ? 'bg-gray-100 border-transparent' : '',
                        )}
                        style={{ zIndex: LOADING_STEPS_DATA.length - index }}
                      >
                        <span className={cn("text-sm font-medium flex-1 mr-3", !step.isFading && step.status === 'completed' ? 'text-green-700' : 'text-gray-700')}>{step.text}</span>
                        {!step.isFading && step.status === 'processing' && (<Loader2 className="h-4 w-4 text-gray-500 animate-spin" />)}
                        {!step.isFading && step.status === 'completed' && (<CheckCircle2 className="h-4 w-4 text-green-600" />)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Welcome Text View */}
              <div
                className={cn(
                  "absolute inset-0 w-full",
                  "transition-opacity ease-in-out",
                  `duration-[${ANIMATION_VIEW_TRANSITION_DURATION}ms]`,
                   activelyShowingWelcome ? "opacity-100" : "opacity-0 pointer-events-none -z-10"
                )}
              >
                <div ref={welcomeTextContentRef} className="text-center">
                  <h2 className="text-2xl font-semibold text-black mb-2">Welcome to Hermes!</h2>
                  <p className="text-gray-700 mb-5 max-w-md text-sm mx-auto">I'm here to assist you with information about Edwin. How can I help you today?</p>
                  <div className="text-xs text-gray-600 space-y-1">
                    <p className="font-medium">You can try asking:</p>
                    <p>"What projects has Edwin worked on?"</p>
                    <p>"Describe Edwin's technical skills."</p>
                    <p>"Tell me about Edwin's experience."</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Main Chat Area */}
      <div
        className={cn(
          "flex-1 overflow-y-auto pt-6 md:pt-16 space-y-4 px-2 md:px-6",
        )}
        style={{ paddingBottom: `${messageListPaddingBottom + 18}px` }}
      >
        {responseMode === 'chat' ? (
          messages.length > 0 && (
            <ChatMessageList className="h-full">
              {messages.map((message) => {
                const variant = message.sender === SENDER_USER ? "sent" : "received";
                return (
                  <ChatBubble key={message.id} variant={variant} className="transition-all duration-300">
                    {variant === "received" && (
                      <ChatBubbleAvatar
                        fallback="H"
                        className="p-1 rounded-full border border-gray-200 w-9 h-9 flex items-center justify-center"
                      >
                        {message.sender === SENDER_BOT && message.isLoading ? (
                          <Loader2 className="h-4 w-4 text-gray-600 animate-spin" />
                        ) : (
                          <GiWingfoot className="h-4 w-4 text-gray-600" />
                        )}
                      </ChatBubbleAvatar>
                    )}
                    <ChatBubbleMessage
                      className={cn(
                        `text-sm py-2 px-3 rounded-lg min-h-[calc(1.25rem+1rem)] flex items-center`,
                        variant === "received" ? "bg-gray-100 text-gray-800" : "bg-black text-white"
                      )}
                    >
                      {message.sender === SENDER_BOT && message.isLoading ? (
                        <TypingIndicator />
                      ) : (
                        message.message
                      )}
                    </ChatBubbleMessage>
                  </ChatBubble>
                );
              })}
              <div className="h-px" ref={messagesEndRef} />
            </ChatMessageList>
          )
        ) : (
          <div className="flex flex-col items-center justify-center h-full min-h-[300px] py-10">
            <OrbitalDataCloud ref={orbitalDataCloudRef} hideControls={true} initialAudioUrl={"https://storage.googleapis.com/ashes_project_website_artifacts/audio/ChampionChallenger.wav"} /> 
            {lastUserMessage && (
              <p className="bg-black rounded-lg p-3 text-gray-600 text-sm mb-4 text-center">
               <span className="font-medium text-gray-200 break-words">{lastUserMessage}</span>
            </p>
            )}
          </div>
        )}
      </div>

      <Sheet open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        {/* Input Field Area Container */}
        <div
          ref={inputAreaRef}
          className={cn(
            "fixed z-20 transition-all duration-200 ease-out md:bottom-6 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-2xl lg:max-w-3xl md:bg-transparent md:border-none md:p-0",
            isMobileView ? cn(
              "left-0 right-0 px-3 sm:px-4 py-2 bg-white border-t",
              isKeyboardOpen ? "bottom-0" : MOBILE_NAVBAR_OFFSET_CLASS
            ) : ""
          )}
        >
          <ChatInput
            value={currentMessage}
            onChange={handleInputChange}
            onSubmit={handleInputSubmit}
            placeholder="Ask me anything about Edwin..."
            isLoading={isLoadingApi || isProcessingMessage} 
            responseMode={responseMode}
            onToggleMode={() => handleResponseModeChange(responseMode !== 'voice')}
          />
        </div>

        <SheetContent 
          side="right" 
          className="w-[300px] bg-white p-0 flex flex-col z-[9999] [&>button]:hidden overflow-hidden"
        >
          <SheetTitle className="sr-only">Hermes Settings</SheetTitle>
          
          {/* Header with Logo */}
          <div className="border-b border-gray-100 px-4 py-3">
            <div className="flex items-center space-x-3">
              <GiWingfoot className="h-5 w-5 text-gray-800" />
              <div>
                <h2 className="text-base font-medium text-gray-900">Hermes</h2>
                <p className="text-xs text-gray-500">Settings</p>
              </div>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
            {/* Response Mode Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">Response Mode</h3>
              <p className="text-xs text-gray-500 mb-3">Choose how you'd like to receive responses from Hermes.</p>
              <button
                onClick={() => handleResponseModeChange(!(responseMode === 'voice'))}
                className={cn(
                  `w-full text-left p-3 transition-all duration-150 rounded-md border`,
                  responseMode === 'voice' 
                    ? 'bg-blue-50 hover:bg-blue-100 border-blue-200 text-blue-700' 
                    : 'bg-gray-100 hover:bg-gray-200 border-gray-200 text-gray-800',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50'
                )}
              >
                <div className="flex items-start space-x-3">
                  <div className={cn(
                    `p-2 rounded-full`,
                    responseMode === 'voice' ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
                  )}>
                    {responseMode === 'voice' ? (
                      <Mic className="h-5 w-5" />
                    ) : (
                      <MessageSquare className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      {responseMode === 'voice' ? 'Voice Response' : 'Text Response'}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {responseMode === 'voice' ? 'Hear responses as voice' : 'Read responses as text'}
                    </p>
                  </div>
                </div>
              </button>
            </div>
            
            <div className="h-px bg-gray-200"></div>
            
            {/* Clear Chat Section */}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">Chat History</h3>
                <p className="text-xs text-gray-500 mb-3">
                  Clear all messages and start a new conversation.
                </p>
                <button
                  onClick={handleClearChat}
                  className="w-full text-left p-3 transition-all duration-150 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-opacity-50 rounded-md border border-gray-200 text-gray-800"
                >
                  <div className="flex items-start space-x-3">
                    <div className="p-2 rounded-full bg-gray-200 text-gray-600">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm">Clear Chat</p>
                    </div>
                  </div>
                </button>
            </div>
          </div>

          {/* Footer with Close Button */}
          <div className="border-t border-gray-200 px-4 py-3">
            <button 
              className="w-full rounded-md border-2 border-black bg-black text-white py-2.5 text-sm font-medium relative overflow-hidden transition-all duration-200 hover:after:content-[''] hover:after:absolute hover:after:inset-0 hover:after:bg-white hover:after:opacity-20 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1 focus:ring-offset-white"
              onClick={() => setIsSidebarOpen(false)}
            >
              Close
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default ChatComponent;